import { camelizeKeys } from 'humps'
import isEmpty from 'lodash.isempty'

import { COUNTRIES } from '@config/constants'
import { sortByAlphabetical } from '@core/helpers'
import {
  getCountryCodeFromLocale,
  getCountryCodeFromLocaleOrCountryCode,
  getCurrencyFromCountryCode,
} from '@core/helpers/countries'
import { formatDateToYYYYMMDD } from '@core/helpers/date'
import { getFrequency } from '@kyc/Profile/components/Preferences/helpers'
import { ALL_CATEGORIES } from '@merchant/pages/international-onboarding/InternationalOnboarding.constant'

/**
 * @typedef {Object} Price Read more: https://github.com/BackMarket/api-design-guidelines/blob/develop/models/API-Model-Price.md
 * @property {string} currency See `CURRENCIES`. Ex: "USD"
 * @property {string} amount Ex: "123.45"
 */

// Do not camelize everything, because some of the keys are sent back to the API
// or Algolia, and transforming them would confuse those external services.
export const mapFromAlgoliaRefinements = ({
  facets,
  top_filters, // eslint-disable-line camelcase
  numeric_filters, // eslint-disable-line camelcase
  ...data
}) => ({
  ...data,
  facets: camelizeKeys(facets),
  topFilters: camelizeKeys(top_filters),
  numericFilters: camelizeKeys(numeric_filters),
})

export const mapFromLandingDetails = (payload) => ({
  slug: payload.slug,
  // Product specific
  bannerInfo: camelizeKeys(payload.banner_info),
  id: String(payload.id),
  urls: payload.urls,
  links: payload.links,
  link: payload.link,
  hero: payload.hero,
  tagline: payload.tagline,
  seo: {
    title: payload.title_seo,
    text: payload.text_seo,
  },
  electronicWaste: !isEmpty(payload.electronic_waste)
    ? {
        weight: payload.electronic_waste.weight,
        chips: payload.electronic_waste.chips_number,
      }
    : {},
  buyingGuide: payload.buying_guide,
  subtitle: payload.displayed_title,
  title: payload.displayed_title_plural,
  displayHeroAndRates: payload.display_hero_and_rates,
  type: payload.landing_type,
  listView: payload.list_view,
  listViewStructure: payload.list_view_structure,
  // Merchant specific
  address: payload.address,
  company: payload.company,
  deltaAged: payload.delta_aged,
  orderlinesLen: payload.orderlines_len,
  legalName: payload.merchant_legal_name,
  shopCoverPicture: payload.shop_cover_picture,
  description: payload.description,
  savContact: payload.sav_contact,
  vatNumber: payload.vat_number,
  siretNumber: payload.siret_number,
  isShownMerchantInfo: Boolean(payload.display_merchant_infos),
  isVanishingDeals: payload.isVanishingDeals,
})

/* Payment */

export const DISCOUNT_TYPES = {
  NOT_VALID: 0,
  WITHOUT_REFERRAL: 1,
  WITH_REFERRAL: 3,
  REFERRAL_BASE: 5,
}

/**
 * TODO: Migrate to Price model
 * @typedef Discount
 * @property {string} id
 * @property {number} deductionMax
 * @property {number} deduction
 * @property {number} deductedPrice
 * @property {number} referralBase
 *
 * @param {Object} payload
 * @returns {Discount}
 */
const mapFromValidDiscount = (payload) => {
  const amount = Number(payload.amount)

  return {
    id: payload.promotion_code_pk,
    deductionMax: amount,
    deduction: amount,
    deductedPrice: Number(payload.new_price),
    referralBase: DISCOUNT_TYPES.REFERRAL_BASE,
  }
}

/**
 * @param {Object} payload
 * @typedef {{ isValid: true } & Discount} ValidDiscount
 * @typedef {{ isValid: false, errorMessage: string }} InvalidDiscount
 * @returns {ValidDiscount|InvalidDiscount}
 * @throws {Error} If discount type returned by API is unknown.
 */
export const mapFromDiscount = (payload) => {
  const { valid: type, ...data } = payload

  switch (type) {
    case DISCOUNT_TYPES.WITH_REFERRAL:
    case DISCOUNT_TYPES.WITHOUT_REFERRAL:
      return {
        isValid: true,
        ...mapFromValidDiscount(data),
      }

    case DISCOUNT_TYPES.NOT_VALID:
      return {
        isValid: false,
        errorMessage: data.message,
      }

    default:
      throw new Error(`Unknown discount type: ${type}`)
  }
}

/**
 * @typedef {Object} Installment
 * @property {string} date Date in yyyy-mm-dd format
 * @property {Price} price
 *
 * @param {Object} installment
 * @param {string} currency
 * @returns Installment
 */
const mapFromInstallment = (installment, currency) => ({
  date: formatDateToYYYYMMDD(installment.collection_date),
  price: {
    currency,
    amount: String(installment.amount),
  },
})

/**
 * @typedef {Object} InstallmentSimulation
 * @property {Price} initial installment + initial fees
 * @property {Price} fees
 * @property {Installment[]} installments
 * @property {Price} total Sum of `initial` + all `installments[n].amount`
 *
 * @param {Object} payload
 * @param {string} currency
 * @returns {InstallmentSimulation}
 */
export const mapFromInstallments = (payload, currency) => ({
  initial: {
    currency,
    amount: String(payload.down_amount),
  },
  fees: {
    currency,
    amount: String(payload.cost),
  },
  installments: payload.installments.map((installment) =>
    mapFromInstallment(installment, currency),
  ),
  total: {
    currency,
    amount: payload.total_amount,
  },
})

/* Profile */

export const mapFromNewsletterPreferences = (preferences) =>
  preferences.map((data) => ({
    userPreferenceId: data.email_preference,
    newsletterId: data.marketing_newsletter_id,
    newsletterName: data.marketing_newsletter_name,
    newsletterChoices: data.marketing_newsletter_choices,
    optin: data.optin,
    frequency: getFrequency(data),
  }))

/* Customer Request (SAV) CMS Templates */

export const mapFromBuyingGuides = (payload) => {
  return {
    ...payload,
    countryCode: getCountryCodeFromLocale(payload.country_code),
  }
}

export const mapFromBuyingGuidesHub = (payload) => {
  return {
    ...payload,
    countryCode: getCountryCodeFromLocale(payload.country_code),
  }
}

/* Reviews */
export const mapReviewsListFromApi = ({ results = [], ...data }) => ({
  ...data,
  results: results.map((review) => {
    return {
      reviewId: review.reviewOrderLineId,
      reviewInfo: {
        rate: Number(review.rate),
        comment: review.comment,
        dateReview: review.dateReview,
      },
      characteristics: review.reviews || [],
      userInfo: {
        firstName: review.firstName,
        lastName: review.lastName,
        nbClientOrders: review.nbClientOrders,
      },
      productInfo: {
        imageUrl: review.imageUrl,
        productUrl: review.productUrl,
        merchantName: review.merchantName,
        productTitle: review.productTitle,
        modelTitle: review.modelTitle,
        link: review.product?.link || {},
      },
    }
  }),
})

export const mapReviewsSummaryFromApi = (summary = {}) => ({
  statsInfo: summary.rate_distribution
    ? summary.rate_distribution.map((stat) => ({
        percentage: stat.percentage,
        rateRange: stat.rate_range,
      }))
    : [],
})

function getFormattedMerchantReviews(merchantReviews = {}) {
  if (isEmpty(merchantReviews)) {
    return null
  }

  return Object.entries(merchantReviews).reduce(
    (formattedMerchantReviews, [key, value = {}]) => ({
      ...formattedMerchantReviews,
      [key]: {
        averageRate: value.average_rate,
        merchantSlug: value.merchant_slug,
        reviewsCount: value.reviews_count,
      },
    }),
    {},
  )
}
export const mapProductRateFromApi = (data = {}) => ({
  description: data.description || null,
  merchantsReviews: getFormattedMerchantReviews(data.merchants_reviews),
})

/* Merchant Journey, Sourcing auctions */

/**
 * Transform the data received from the API to better match our standards.
 * TODO: remove this mapping when MJCAT-816 will be in prod.
 *
 * @param {Object} data
 * @return {Object}
 */
export const mapFromGetOpportunities = (data) => {
  return 'results' in data
    ? {
        ...data,
        results: data.results.map((result) => ({
          ...result,
          merchant_offer: result.merchant_offer || {},
          merchant_best_offer_price: result.merchant_best_offer_price || 0,
          merchant_best_offer_price_with_currency:
            result.merchant_best_offer_price_with_currency || '',
          bottom_price: result.bottom_price || 0,
          bottom_price_with_currency: result.bottom_price_with_currency || '',
        })),
      }
    : data
}

export const mapFromKybProfile = (data) => ({
  legalName: data.pseudo || '',
  displayName: data.company || '',
  email: data.business_mail || '',
  phone: data.address__phone || '',
  address: data.address__street || '',
  city: data.address__city || '',
  zipCode: data.address__postal_code || '',
  state: data.address__state_or_province || '',
  country: data.address__country || '',
  ein: data.vat_number || '',
})

export const mapFromKybBank = (data) => ({
  accountNumber: data.merchant__wallet__account_number || '',
  routingNumber: data.merchant__wallet__branch_code || '',
  owner: data.owner_name || '',
  currencyCode: data.currency || '',
  countryCode: data.country_code || COUNTRIES.US,
  // We do not handle the file received from the API. This will cause the user to
  // upload its file again if they ever want to submit the form one more time.
  // This is far from ideal, but it's simpler for now.
  statement: '',
})

export const mapFromKybOwner = (data) => ({
  firstName: data.address__first_name || '',
  lastName: data.address__last_name || '',
  birthdate: data.address__birthdate || '',
  address: data.address__street || '',
  city: data.address__city || '',
  zipCode: data.address__postal_code || '',
  gender: data.address__gender !== null ? data.address__gender.toString() : '',
  state: data.address__state_or_province || '',
  country: data.address__country || '',
  email: data.address__email || '',
  ssn: data.merchant__social_security_number
    ? data.merchant__social_security_number.slice(-4)
    : '',
})

export const mapFromAllCategories = ({ results: categories }) =>
  camelizeKeys(categories)
    .filter(({ categoryName }) => !isEmpty(categoryName))
    .sort((a, b) => {
      if (a.categoryName < b.categoryName) return -1
      if (a.categoryName > b.categoryName) return 1

      return 0
    })

// TODO remove when CMS API is available
export const mapFromProductBanner = (data) => ({
  currency: data.listing.currency,
  image: data.image,
  originalPrice: data.price_original,
  price: data.listing.price,
  cta: { link: data.link },
  title: data.model_name,
})

export const mapProductReviews = (data) => ({
  ...data,
  rate: Number(data.rate),
})

export const mapFromInternationalOnboarding = (payload) => {
  const finalPayload = { ...payload }

  if (!isEmpty(payload.markets)) {
    finalPayload.markets = payload.markets.map(
      getCountryCodeFromLocaleOrCountryCode,
    )
  }

  if (!isEmpty(payload.customerCare)) {
    finalPayload.customerCare = payload.customerCare.map(
      ({ market, ...rest }) => ({
        ...rest,
        market: getCountryCodeFromLocaleOrCountryCode(market),
      }),
    )
  }

  if (!isEmpty(payload.shipping)) {
    finalPayload.shipping = [
      ...payload.shipping.categoryShipping,
      ...payload.shipping.globalShipping.map((method) => ({
        ...method,
        category: ALL_CATEGORIES,
      })),
      // ! for the backend, a countryCode is a locale
    ].map(({ countryCode: locale, ...method }) => ({
      ...method,
      countryCode: getCountryCodeFromLocaleOrCountryCode(locale),
    }))
  }

  if (!isEmpty(payload.payment)) {
    finalPayload.payment = payload.payment.map(({ market, ...rest }) => ({
      ...rest,
      market: getCountryCodeFromLocaleOrCountryCode(market),
    }))
  }

  if (!isEmpty(payload.pricingRules) && !isEmpty(payload.pricingRules.rules)) {
    finalPayload.pricingRules = {
      rules: payload.pricingRules.rules.map((rule) => ({
        ...rule,
        targetMarket: getCountryCodeFromLocaleOrCountryCode(rule.targetMarket),
      })),
    }
  }

  if (!isEmpty(payload.metaData)) {
    if (payload.metaData.openableMarkets) {
      finalPayload.metaData.openableMarkets =
        payload.metaData.openableMarkets.map(
          getCountryCodeFromLocaleOrCountryCode,
        )
    }

    if (payload.metaData.referenceMarket) {
      finalPayload.metaData.referenceMarket =
        getCountryCodeFromLocaleOrCountryCode(payload.metaData.referenceMarket)
    }

    if (payload.metaData.marketsNeedKybCheck) {
      finalPayload.metaData.marketsNeedKybCheck =
        payload.metaData.marketsNeedKybCheck.map(
          getCountryCodeFromLocaleOrCountryCode,
        )
    }

    // For metadata by country
    const localeRegex = /[a-z]{2}-[a-z]{2}$/

    Object.keys(payload.metaData).forEach((key) => {
      if (localeRegex.test(key)) {
        finalPayload.metaData[getCountryCodeFromLocaleOrCountryCode(key)] =
          payload.metaData[key]
      }
    })
  }

  return finalPayload
}

// TODO: clean the mapFromGetListings once the API endpoint applies the changes (MCC-2650)
const grades = ['shiny', 'gold', 'silver', 'bronze', 'stallone']

const transformListing = ({ markets, state, quantity, ...listing }) => {
  const marketsWithCountryCodes = Object.entries(markets).map(
    ([locale, market]) => {
      const countryCode = getCountryCodeFromLocaleOrCountryCode(locale)

      return {
        ...market,
        countryCode,
        currency: getCurrencyFromCountryCode(countryCode),
      }
    },
  )

  const marketsWithCountryCodesOrdered = sortByAlphabetical(
    marketsWithCountryCodes,
    'countryCode',
  )

  return {
    ...listing,
    markets: marketsWithCountryCodesOrdered,
    state: grades[state],
    stock: quantity,
  }
}

export const mapFromUpdateListing = (payload) => transformListing(payload)

export const mapFromGetListings = (payload) => ({
  ...payload,
  results: payload.results.map(transformListing),
})

export const mapFromFetchMerchantData = (payload) => ({
  ...payload,
  activeMarkets: payload.activeMarkets.map(
    getCountryCodeFromLocaleOrCountryCode,
  ),
  potentialMarkets: payload.potentialMarkets.map(
    getCountryCodeFromLocaleOrCountryCode,
  ),
  referenceMarket: getCountryCodeFromLocaleOrCountryCode(
    payload.referenceMarket,
  ),
})

export const mapFromGetOnlineConditions = (payload) =>
  Object.keys(payload).reduce(
    (acc, locale) => ({
      ...acc,
      [getCountryCodeFromLocaleOrCountryCode(locale)]: payload[locale],
    }),
    {},
  )

export const mapFromPricingRule = (payload) => ({
  ...payload,
  targetMarket: getCountryCodeFromLocaleOrCountryCode(payload.targetMarket),
})

export const mapFromGetMarketPricingRules = (payload) => ({
  ...payload,
  results: payload.results.map(mapFromPricingRule),
})

export const mapFromChangeReferenceMarket = (payload) => ({
  referenceMarket: getCountryCodeFromLocaleOrCountryCode(
    payload.referenceMarket,
  ),
})

export const mapFromMerchantPreferences = (payload) => ({
  markets: Object.keys(payload.markets).reduce(
    (acc, locale) => ({
      ...acc,
      [getCountryCodeFromLocaleOrCountryCode(locale)]: payload.markets[locale],
    }),
    {},
  ),
})

export const mapFromGetMerchantPaymentData = (payload) => ({
  ...payload,
  paymentMethods: payload.paymentMethods.map((method) => ({
    ...method,
    availableMarkets: method.availableMarkets.map(
      getCountryCodeFromLocaleOrCountryCode,
    ),
  })),
  payoutInformation: payload.payoutInformation.map((info) => ({
    ...info,
    market: getCountryCodeFromLocaleOrCountryCode(info.market),
  })),
})

export const mapFromGetMerchantPayPalSettings = (payload) => ({
  ...payload,
  payPalInformation: isEmpty(payload.payPalInformation)
    ? null
    : {
        ...payload.payPalInformation,
        markets: payload.payPalInformation.markets.map(
          ({ market, ...rest }) => ({
            ...rest,
            market: getCountryCodeFromLocaleOrCountryCode(market),
          }),
        ),
      },
})

export const mapFromGetMerchantShippingMethods = (payload) => ({
  ...payload,
  shippingMethods: payload.shippingMethods.map(
    ({ market, marketShippingMethods }) => ({
      market: getCountryCodeFromLocaleOrCountryCode(market),
      marketShippingMethods: marketShippingMethods.map((shipping) => ({
        ...shipping,
        market: getCountryCodeFromLocaleOrCountryCode(shipping.market),
      })),
    }),
  ),
})

export const mapFromGetAddressRules = (payload) => ({
  ...payload,
  results: payload.results.map((rule) => ({
    ...rule,
    markets:
      rule.markets === null
        ? null
        : rule.markets.map(getCountryCodeFromLocaleOrCountryCode),
  })),
})

export const mapFromCreateAddressRule = (payload) => {
  const finalPayload = { ...payload }

  if (payload.markets) {
    finalPayload.markets = payload.markets.map(
      getCountryCodeFromLocaleOrCountryCode,
    )
  }

  if (payload.metadata && payload.metadata.markets) {
    finalPayload.metadata.markets = payload.metadata.markets.map(
      getCountryCodeFromLocaleOrCountryCode,
    )
  }

  return finalPayload
}
